import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { IMedicalReferral } from 'API/medicalReferrals';
import { dotDateFormatMM, shortDateFormat } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import { Download2 } from 'Images';
import { format, isAfter, isBefore } from 'date-fns';

interface IReferralSummary {
  referral: IMedicalReferral;
  downloadPdf: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => Promise<void>;

  isLoading: boolean;
}

export const ReferralSummary = ({
  referral,
  downloadPdf,
  isLoading,
}: IReferralSummary) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.MedicalReferrals);
  const { hasFile, isActive, openDate, part } = referral;
  const { loader } = useLoader('', { height: '50px', margin: '0 auto' }, 40);

  const dateStatusColor = {
    [dictionary.referral.dateStatus.invalid]: 'orange.shade_200',
    [dictionary.referral.dateStatus.valid]: '#E0F6EF',
    [dictionary.referral.dateStatus.future]: 'primary.shade_100',
  };

  const ColorByReferralStatus = {
    [dictionary.referral.status.review]: {
      backgroundColor: '#FFF7EA',
      borderColor: '#FFEDCE',
    },
    [dictionary.referral.status.opened]: {
      backgroundColor: 'primary.shade_100',
      borderColor: 'primary.shade_200',
    },
    [dictionary.referral.status.approved]: {
      backgroundColor: '#E0F6EF',
      borderColor: '#B8EAD9  ',
    },
    [dictionary.referral.status.postponed]: {
      backgroundColor: 'orange.shade_100',
      borderColor: 'orange.shade_200',
    },
    [dictionary.referral.status.reject]: {
      backgroundColor: 'orange.shade_100',
      borderColor: 'orange.shade_200',
    },
  };

  const referralStatusByStatusCode: { [key: number]: string } = {
    1: dictionary.referral.status.opened,
    4: dictionary.referral.status.review,
    5: dictionary.referral.status.approved,
    7: dictionary.referral.status.review,
    8: isActive
      ? dictionary.referral.status.postponed
      : dictionary.referral.status.reject,
  };

  const getDateStatus = (from: Date, to: Date) => {
    if (!to || !from) return null;
    const now = Date.now();

    if (isAfter(from, now) && isActive)
      return dictionary.referral.dateStatus.future;

    if (isAfter(to, now) && isActive)
      return dictionary.referral.dateStatus.valid;

    return dictionary.referral.dateStatus.invalid;
  };

  const referralStatus = referralStatusByStatusCode[referral.statusCode];

  const validFromDate =
    referral.validFromDate && new Date(referral.validFromDate);

  const validToDate = referral.validToDate && new Date(referral.validToDate);
  const dateStatus =
    validFromDate && validToDate && getDateStatus(validFromDate, validToDate);

  const stateBackgroundColor =
    ColorByReferralStatus[referralStatus].backgroundColor;

  const stateBorderColor = ColorByReferralStatus[referralStatus].borderColor;
  const formattedOpenDate = format(new Date(openDate), dotDateFormatMM);
  const formattedFromDate =
    referralStatus !== dictionary.referral.status.review &&
    validFromDate &&
    format(validFromDate, dotDateFormatMM);
  const isFutureReferral = dateStatus === dictionary.referral.dateStatus.future;
  const formattedToDate =
    validToDate &&
    dateStatus !== dictionary.referral.dateStatus.future &&
    format(validToDate, dotDateFormatMM);

  const getValidationDateTextTitle = (fromDate?: Date, toDate?: Date) => {
    if (fromDate && toDate) {
      const today = new Date(Date.now());
      if (
        isBefore(today, new Date(validToDate)) &&
        isBefore(new Date(validFromDate), today)
      )
        return {
          date: format(new Date(validToDate), shortDateFormat),
          title: dictionary.endOfValidity,
        };
      if (isBefore(today, new Date(validFromDate)))
        return {
          date: format(new Date(validFromDate), shortDateFormat),
          title: dictionary.startOfValidity,
        };
    }
    return { date: '', title: '' };
  };

  const validationDateTitle = getValidationDateTextTitle(
    referral.validFromDate,
    referral.validToDate
  );

  if (isDesktop)
    return (
      <Stack>
        {dateStatus && (
          <Box position={'absolute'} top={'0'} left={'0'}>
            <Chip
              label={dateStatus}
              sx={{
                backgroundColor: dateStatus
                  ? dateStatusColor[dateStatus]
                  : 'transparent',
                borderRadius: '16px 0px',
                padding: '6px 24px',
                fontSize: '16px',
                fontWeight: 600,
              }}
            />
          </Box>
        )}
        <Stack>
          <Stack
            direction={'row'}
            alignItems={'flex-start'}
            gap={isDesktop ? '40px' : '20px 24px'}
          >
            <Stack
              gap={'8px'}
              width={'275px'}
              flexShrink={0}
              position={'relative'}
              top={'10px'}
            >
              <Typography variant={'body6'}>
                {dictionary.referral.subject}
              </Typography>
              <Typography fontSize={'18px'} fontWeight={700}>
                {part}
              </Typography>
            </Stack>
            <Stack gap={'10px'} width={'91px'} flexShrink={0}>
              <Typography variant={'body6'}>
                {dictionary.referral.requestDate}
              </Typography>
              <Typography variant={'body2'}>{formattedOpenDate}</Typography>
            </Stack>
            <Stack gap={'10px'} width={'128px'} flexShrink={0}>
              <Typography variant={'body6'}>
                {dictionary.referral.requestState}
              </Typography>
              <Badge
                sx={{
                  border: '1px solid',
                  backgroundColor: referral.isActive
                    ? stateBackgroundColor
                    : 'orange.shade_100',
                  borderColor: referral.isActive
                    ? stateBorderColor
                    : 'orange.shade_200',
                  borderRadius: '6px',
                  padding: '0 12px 3px',
                  width: 'fit-content',
                }}
              >
                <Typography variant={'body5'} whiteSpace={'nowrap'}>
                  {referral.isActive
                    ? referralStatus
                    : dictionary.referral.status.reject}
                </Typography>
              </Badge>
            </Stack>
            <Stack gap={'10px'} width={'137px'} flexShrink={0}>
              <Typography variant={'body6'}>
                {validationDateTitle.title}
              </Typography>
              <Typography variant={'body2'}>
                {validationDateTitle.date}
              </Typography>
            </Stack>
            {hasFile &&
              isActive &&
              !!referral.id &&
              (isLoading ? (
                <Button
                  onClick={downloadPdf}
                  sx={{
                    backgroundColor: 'primary.shade_100',
                    border: '1px solid rgba(183, 204, 255, 0.2)',
                    borderRadius: '100px',
                    padding: '12px 24px',
                    width: '118px',
                    height: '46px',
                    gap: '10px',
                    flexShrink: 0,
                  }}
                >
                  <Avatar
                    alt={''}
                    src={Download2}
                    sx={{ width: '20px', height: '20px' }}
                  />
                  <Typography variant={'body4'}>
                    {dictionary.referral.download}
                  </Typography>
                </Button>
              ) : (
                loader
              ))}
          </Stack>
        </Stack>
      </Stack>
    );

  return (
    <Stack>
      {dateStatus && (
        <Box position={'absolute'} top={'0'} left={'0'}>
          <Chip
            label={dateStatus}
            sx={{
              backgroundColor: dateStatus
                ? dateStatusColor[dateStatus]
                : 'transparent',
              borderRadius: '16px 0px',
              padding: '6px 24px',
              fontSize: '16px',
              fontWeight: 600,
            }}
          />
        </Box>
      )}
      <Stack gap={'12px'}>
        <Stack gap={'4px'}>
          <Typography variant={'body7'}>
            {dictionary.referral.subject}
          </Typography>
          <Typography variant={'body1'}>{part}</Typography>
        </Stack>
        <Divider
          aria-hidden={'true'}
          orientation={'horizontal'}
          sx={{ borderColor: 'primary.shade_100' }}
        />
        <Stack direction={'row'} flexWrap={'wrap'} gap={'20px'}>
          <Stack gap={'4px'} width={'135px'} flexShrink={0}>
            <Typography variant={'body6'}>
              {dictionary.referral.requestDate}
            </Typography>
            <Typography variant={'body2'}>{formattedOpenDate}</Typography>
          </Stack>
          <Stack gap={'4px'} width={'135px'} flexShrink={0}>
            <Typography variant={'body6'}>
              {dictionary.referral.requestState}
            </Typography>
            <Badge
              sx={{
                border: '1px solid',
                backgroundColor: referral.isActive
                  ? stateBackgroundColor
                  : 'orange.shade_100',
                borderColor: referral.isActive
                  ? stateBorderColor
                  : 'orange.shade_200',
                borderRadius: '6px',
                padding: '0 12px 3px',
                width: 'fit-content',
              }}
            >
              <Typography variant={'body5'} whiteSpace={'nowrap'}>
                {referral.isActive
                  ? referralStatus
                  : dictionary.referral.status.reject}
              </Typography>
            </Badge>
          </Stack>
          {!isFutureReferral &&
            formattedToDate &&
            dateStatus !== dictionary.referral.dateStatus.invalid && (
              <Stack gap={'4px'} width={'295px'} flexShrink={0}>
                <Typography variant={'body6'}>
                  {validationDateTitle.title}
                </Typography>
                <Typography variant={'body2'}>
                  {validationDateTitle.date}
                </Typography>
              </Stack>
            )}
          {isFutureReferral && formattedFromDate && (
            <Stack gap={'10px'} width={'295px'} flexShrink={0}>
              <Typography variant={'body6'}>
                {dictionary.referral.validFromDateFull}
              </Typography>
              <Typography variant={'body2'}>{formattedFromDate}</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
